import axios, { AxiosResponse } from 'axios'
import DOMPurify from 'dompurify'
import type { ThemeApiResponse, BannerLogoApiResponse, PropertyInfo } from '@/types/theme'
import { localStorageVariables } from '@/types/localStorageVariables'
import { apiClientSecure } from '@/lib/api/index'
import { applyFavicon } from '@/lib/faviconUtils'

// CSS caching constants
const CSS_CACHE_DURATION = 20 * 60 * 1000 // 20 minutes in milliseconds

// List of URL segments that should not be treated as site names
const INVALID_SITE_NAMES = ['login', 'dashboard', 'settings', 'applicantinfo', 'ole']

// Helper function to determine the actual site name to send to API
function getEffectiveSiteName(rawSiteName: string | null): string {
  // If no site name or it's an invalid one, use 'default'
  if (!rawSiteName || INVALID_SITE_NAMES.includes(rawSiteName.toLowerCase())) {
    return 'default'
  }
  return rawSiteName
}

/**
 * Get color scheme and property information - matches Angular getColorScheme method
 * This is the primary API call that replaces both site information and theme CSS calls
 */
export async function getColorScheme(siteName: string): Promise<ThemeApiResponse> {
  try {
    console.log('🎨 Calling getColorScheme API for siteName:', siteName)

    const effectiveSiteName = getEffectiveSiteName(siteName)
    const response: AxiosResponse<ThemeApiResponse> = await apiClientSecure.get(
      `themeloader/getcustomcolorsfilename?siteName=${encodeURIComponent(effectiveSiteName)}`
    )

    console.log('🎨 getColorScheme response:', response.data)
    return response.data
  } catch (error) {
    console.error('❌ getColorScheme failed:', error)

    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load theme'
      throw new Error(errorMessage)
    }

    throw new Error('An unexpected error occurred while loading theme')
  }
}

/**
 * Get banner and property logo URLs - matches Angular onGetBannerAndPropertyLogoUrl method
 */
export async function getBannerAndPropertyLogoUrls(siteName: string, propertyId: string): Promise<BannerLogoApiResponse> {
  try {
    console.log('🖼️ Calling getBannerAndPropertyLogoUrls API for siteName:', siteName, 'propertyId:', propertyId)

    const effectiveSiteName = getEffectiveSiteName(siteName)
    const response: AxiosResponse<BannerLogoApiResponse> = await apiClientSecure.get(
      `themeloader/getbannerandpropertylogoimageurl?siteName=${encodeURIComponent(effectiveSiteName)}&propertyId=${encodeURIComponent(propertyId)}`
    )

    console.log('🖼️ getBannerAndPropertyLogoUrls response:', response.data)

    // Automatically store successful responses in localStorage (matching Angular behavior)
    if (response.data && String(response.data.status).toUpperCase() === 'OK') {
      if (response.data.PropertyLogoUrl) {
        localStorage.setItem(localStorageVariables.PropertyLogoUrl, response.data.PropertyLogoUrl)
        applyFavicon(response.data.PropertyLogoUrl)
        console.log('💾 Stored PropertyLogoUrl in localStorage:', response.data.PropertyLogoUrl)
      }

      if (response.data.BannerImageUrl) {
        localStorage.setItem(localStorageVariables.PropertyBannerImageUrl, response.data.BannerImageUrl)
        console.log('💾 Stored PropertyBannerImageUrl in localStorage:', response.data.BannerImageUrl)
      }
    }

    return response.data
  } catch (error) {
    console.error('❌ getBannerAndPropertyLogoUrls failed:', error)

    if (axios.isAxiosError(error)) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load banner and logo URLs'
      throw new Error(errorMessage)
    }

    throw new Error('An unexpected error occurred while loading banner and logo URLs')
  }
}

// Request deduplication cache to prevent multiple simultaneous requests
const pendingRequests = new Map<string, Promise<string>>()

/**
 * Fetch and sanitize CSS content from a URL
 */
export async function fetchThemeCSS(cssUrl: string): Promise<string> {
  try {
    console.log('🎨 Fetching theme CSS from URL:', cssUrl)

    // First, check if we have valid cached CSS
    const cachedCSS = getCachedCSS(cssUrl)
    if (cachedCSS) {
      return cachedCSS
    }

    // Check if there's already a pending request for this URL
    if (pendingRequests.has(cssUrl)) {
      console.log('🔄 Reusing existing CSS request for:', cssUrl)
      return await pendingRequests.get(cssUrl)!
    }

    // Build the full URL properly for both development and production
    let fullCssUrl: string;

    if (cssUrl.startsWith('http')) {
      // Already a complete URL
      fullCssUrl = cssUrl;
    } else {
      // Relative URL - need to construct full URL
      // In development: use relative URL for Vite proxy
      // In production: construct full URL based on current window location
      if (import.meta.env.DEV) {
        // Development: use relative URL so Vite can proxy it
        fullCssUrl = cssUrl;
      } else {
        // Production: construct full URL based on current origin
        fullCssUrl = `${window.location.origin}${cssUrl}`;
      }
    }

    console.log('🎨 Full CSS URL:', fullCssUrl)

    // Create the request promise
    const requestPromise = (async () => {
      try {
        // Use fetch instead of axios client to avoid base URL conflicts
        const response = await fetch(fullCssUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'text/css',
          },
          // Add auth token if available
          ...(localStorage.getItem('auth_token') && {
            headers: {
              'Content-Type': 'text/css',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
            }
          })
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch CSS: ${response.status} ${response.statusText}`)
        }

        const cssText = await response.text()

        // Sanitize CSS content
        const sanitizedCss = DOMPurify.sanitize(cssText, {
          ALLOWED_TAGS: [],
          ALLOWED_ATTR: []
        })

        // Cache the sanitized CSS content for 20 minutes
        setCachedCSS(cssUrl, sanitizedCss)

        console.log('✅ CSS fetched, sanitized, and cached successfully')
        return sanitizedCss
      } finally {
        // Remove from pending requests when done
        pendingRequests.delete(cssUrl)
      }
    })()

    // Store the promise to deduplicate requests
    pendingRequests.set(cssUrl, requestPromise)

    return await requestPromise
  } catch (error) {
    // Make sure to clean up on error
    pendingRequests.delete(cssUrl)

    console.error('❌ fetchThemeCSS failed:', error)

    if (error instanceof Error) {
      throw new Error(`Failed to fetch CSS: ${error.message}`)
    }

    throw new Error('An unexpected error occurred while fetching CSS')
  }
}

/**
 * Map Angular service response to React PropertyInfo structure
 */
export function mapThemeResponseToPropertyInfo(response: ThemeApiResponse): PropertyInfo {
  return {
    id: response.propertyId,
    name: response.propertyname || '',
    address: response.propertyaddress || '',
    address2: response.propertyaddress2,
    city: response.propertycity,
    state: response.propertystate,
    zip: response.propertyzip,
    culture: response.propertyculture,
    currencycode: response.propertycurrencycode,
    logoURL: response.logoURL,
    backgroundImageURL: response.backgroundImageURL,
    primaryColor: response.primaryColor,
    secondaryColor: response.secondaryColor,
    accentColor: response.accentColor,
    textColor: response.textColor,
    fontFamily: response.fontFamily
  }
}

/**
 * Apply CSS styles to DOM - matches Angular behavior
 */
export function applyCssStyles(cssContent: string, styleId: string = 'custom-theme-styles'): void {
  try {
    // Remove existing styles first
    removeCssStyles(styleId)

    // Create and append new style element
    const styleElement = document.createElement('style')
    styleElement.id = styleId
    styleElement.textContent = cssContent
    document.head.appendChild(styleElement)
    applyCustomThemeFromCssVars()

    console.log('✅ CSS styles applied to DOM')
  } catch (error) {
    console.error('❌ Error applying CSS styles:', error)
    throw new Error('Failed to apply CSS styles to DOM')
  }
}

/**
 * Remove CSS styles from DOM
 */
export function removeCssStyles(styleId: string = 'custom-theme-styles'): void {
  const existingElement = document.getElementById(styleId)
  if (existingElement) {
    existingElement.remove()
    console.log('✅ CSS styles removed from DOM')
  }
}

/**
 * Traditional theme switching (like Angular dark/light themes)
 */
export function setTraditionalTheme(theme: string, linkId: string = 'traditional-theme-link'): void {
  localStorage.setItem('theme', theme)

  let linkElement = document.getElementById(linkId) as HTMLLinkElement
  if (!linkElement) {
    linkElement = document.createElement('link')
    linkElement.id = linkId
    linkElement.rel = 'stylesheet'
    document.head.appendChild(linkElement)
  }

  linkElement.href = `${theme}.css`
  console.log('✅ Traditional theme set to:', theme)
}

/**
 * Get traditional theme from localStorage
 */
export function getTraditionalTheme(defaultTheme: string = 'dark'): string {
  return localStorage.getItem('theme') || defaultTheme
}

/**
 * Cache CSS content with expiration
 */
export function setCachedCSS(cssUrl: string, cssContent: string): void {
  try {
    const cacheKey = `${cssUrl}|${cssContent.substring(0, 100)}` // Include URL and content hash
    const expiryTime = Date.now() + CSS_CACHE_DURATION

    localStorage.setItem(localStorageVariables.CachedThemeCSS, JSON.stringify({
      key: cacheKey,
      cssUrl,
      content: cssContent,
      timestamp: Date.now()
    }))
    localStorage.setItem(localStorageVariables.CachedThemeCSSExpiry, expiryTime.toString())

    console.log('💾 CSS cached successfully for URL:', cssUrl, 'expires in 20 minutes')
  } catch (error) {
    console.warn('⚠️ Failed to cache CSS content:', error)
  }
}

/**
 * Get cached CSS content if valid and not expired
 */
export function getCachedCSS(cssUrl: string): string | null {
  try {
    const cachedData = localStorage.getItem(localStorageVariables.CachedThemeCSS)
    const expiryTime = localStorage.getItem(localStorageVariables.CachedThemeCSSExpiry)

    if (!cachedData || !expiryTime) {
      return null
    }

    const currentTime = Date.now()
    const expiry = parseInt(expiryTime)

    // Check if cache is expired
    if (currentTime > expiry) {
      console.log('⏰ CSS cache expired, will fetch fresh content')
      clearCachedCSS()
      return null
    }

    const parsedData = JSON.parse(cachedData)

    // Check if the cached CSS is for the same URL
    if (parsedData.cssUrl === cssUrl) {
      const remainingTime = Math.round((expiry - currentTime) / 1000 / 60)
      console.log(`✅ Using cached CSS for URL: ${cssUrl}, expires in ${remainingTime} minutes`)
      return parsedData.content
    }

    console.log('🔄 CSS URL changed, cached CSS not applicable')
    return null
  } catch (error) {
    console.warn('⚠️ Failed to retrieve cached CSS:', error)
    clearCachedCSS()
    return null
  }
}

/**
 * Clear cached CSS content
 */
export function clearCachedCSS(): void {
  localStorage.removeItem(localStorageVariables.CachedThemeCSS)
  localStorage.removeItem(localStorageVariables.CachedThemeCSSExpiry)
  console.log('🗑️ CSS cache cleared')
}

/**
 * React Query compatible hooks using axios
 */

// Hook for getting color scheme (main theme data)
export function useColorScheme(siteName: string | null, enabled = true) {
  const queryKey = ['colorScheme', siteName]

  return {
    queryKey,
    queryFn: () => getColorScheme(siteName!),
      enabled: enabled && !!siteName,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: false
    }
}

// Hook for getting banner and logo URLs
export function useBannerAndLogoUrls(siteName: string | null, propertyId: string | null, enabled = true) {
  const queryKey = ['bannerAndLogo', siteName, propertyId]

  return {
    queryKey,
    queryFn: () => getBannerAndPropertyLogoUrls(siteName!, propertyId!),
      enabled: enabled && !!siteName && !!propertyId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: false
  }
}

// Hook for fetching theme CSS
export function useThemeCSS(cssUrl: string | null, enabled = true) {
  const queryKey = ['themeCSS', cssUrl]

  return {
    queryKey,
    queryFn: () => fetchThemeCSS(cssUrl!),
    enabled: enabled && !!cssUrl,
      staleTime: 30 * 60 * 1000, // 30 minutes - CSS doesn't change often
      gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    refetchOnWindowFocus: false
  }
}

/**
 * Reads --primary and --secondary from :root, generates a simple palette using opacity and contrast, and sets derived CSS variables.
 * Call this after your theme CSS is injected and the variables are available in the DOM.
 */
export function applyCustomThemeFromCssVars() {
  // Helper to get the raw CSS variable value
  function cssVarToHex(cssVar: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
  }

  // Helper: hex to 'r, g, b' string
  function hexToRgbString(hex: string): string {
    hex = hex.replace(/^#/, '');
    let bigint = parseInt(hex, 16);
    let r, g, b;
    if (hex.length === 6) {
      r = (bigint >> 16) & 255;
      g = (bigint >> 8) & 255;
      b = bigint & 255;
    } else if (hex.length === 3) {
      r = ((bigint >> 8) & 15) * 17;
      g = ((bigint >> 4) & 15) * 17;
      b = (bigint & 15) * 17;
    } else {
      return '0, 0, 0';
    }
    return `${r}, ${g}, ${b}`;
  }

  // Helper: best contrast (white or black) as 'r, g, b'
  function getContrastYIQ(hexcolor: string): string {
    hexcolor = hexcolor.replace(/^#/, '');
    if (hexcolor.length === 3) {
      hexcolor = hexcolor.split('').map(x => x + x).join('');
    }
    const r = parseInt(hexcolor.substring(0, 2), 16);
    const g = parseInt(hexcolor.substring(2, 4), 16);
    const b = parseInt(hexcolor.substring(4, 6), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (yiq >= 128) ? '0, 0, 0' : '255, 255, 255';
  }

  // Read the CSS variables (should be hex)
  const primary = cssVarToHex('--primary');
  const secondary = cssVarToHex('--secondary');

  const root = document.documentElement;
  // Set base colors as 'r, g, b'
  root.style.setProperty('--color-sf-primary', hexToRgbString(primary));
  root.style.setProperty('--color-sf-secondary', hexToRgbString(secondary));

  // opacity colors
  root.style.setProperty('--primary-8', 'rgba(var(--color-sf-primary), 0.08)');
  root.style.setProperty('--primary-10', 'rgba(var(--color-sf-primary), 0.1)');
  root.style.setProperty('--primary-30', 'rgba(var(--color-sf-primary), 0.3)');
  root.style.setProperty('--primary-60', 'rgba(var(--color-sf-primary), 0.6)');

  // Containers (same as base, use opacity in CSS via rgba(var(--color-sf-primary), 0.1))
  root.style.setProperty('--color-sf-primary-container', hexToRgbString(primary));
  root.style.setProperty('--color-sf-secondary-container', hexToRgbString(secondary));

  // On-primary/secondary (best contrast, as 'r, g, b' string)
  const onPrimary = getContrastYIQ(primary);
  const onSecondary = getContrastYIQ(secondary);
  
  root.style.setProperty('--color-sf-on-primary', onPrimary);
  root.style.setProperty('--color-sf-on-secondary', onSecondary);

  // On-primary/secondary-container (best contrast for container bg)
  root.style.setProperty('--color-sf-on-primary-container', onPrimary);
  root.style.setProperty('--color-sf-on-secondary-container', onSecondary);
  
  // Set --on-primary and --on-secondary for accessibility/contrast (as 'r, g, b')
  // These are used by SCSS mixins and utility classes for accessible text colors
  root.style.setProperty('--on-primary', onPrimary);
  root.style.setProperty('--on-secondary', onSecondary);
  
  // Set dynamic --primary-foreground and --secondary-foreground based on contrast
  // This ensures text on primary/secondary backgrounds is always readable
  const primaryForeground = `rgb(${onPrimary})`;
  const secondaryForeground = `rgb(${onSecondary})`;
  root.style.setProperty('--primary-foreground', primaryForeground);
  root.style.setProperty('--secondary-foreground', secondaryForeground);
  
  // Debug logging for theme contrast
  console.log('🎨 Theme Contrast Applied:', {
    primary,
    secondary,
    'on-primary': onPrimary,
    'on-secondary': onSecondary,
    'primary-foreground': primaryForeground,
    'secondary-foreground': secondaryForeground
  });
} 